import React from "react";

import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";

import { GreyBorder, GreyState } from "assets/colors";
import { IWorkflowStep } from "model/entities/Workflow";

interface VerticalStepperProps {
  currentStep: number;
  title?: string;
  target_type?: string;
  scope?: string;
  selectedStepForWorkflow?: IWorkflowStep;
  field?: string;
  custom_field1?: string;
  custom_field2?: string;
  option_tag?: string;
  defaultValue?: string;
}

const MetaExpressionStepper: React.FC<VerticalStepperProps> = ({
  currentStep,
  title,
  target_type,
  scope,
  selectedStepForWorkflow,
  field,
  custom_field1,
  custom_field2,
  option_tag,
  defaultValue,
}) => {
  const useMetaStepIconStyles = makeStyles({
    root: {
      color: GreyBorder,
    },
    active: {
      color: GreyBorder,
    },
    completed: {
      color: GreyBorder,
      marginLeft: "-8px",
      fontSize: "40px",
    },
    disabledTitleText: {
      color: GreyBorder,
      fontSize: "20px",
    },
    titleText: {
      color: GreyState,
      fontSize: "20px",
    },
    disabledContent: {
      color: GreyBorder,
      fontSize: "16px",
    },
    contentText: {
      color: GreyState,
      fontSize: "16px",
    },
  });
  const classes = useMetaStepIconStyles();

  function MetaStepIcon() {
    return (
      <div
        className={clsx(classes.root, {
          [classes.active]: true,
        })}
      >
        <FiberManualRecordIcon className={classes.completed} />
      </div>
    );
  }
  return (
    <Stepper activeStep={currentStep} orientation="vertical">
      <Step active>
        <StepLabel>
          <h4 className={classes.titleText}>Details</h4>
        </StepLabel>
        <StepContent>
          <Typography className={classes.contentText}>
            Title: <strong style={{ color: GreyState }}>{title}</strong>
            {/* Title :  */}
          </Typography>
          <Typography className={classes.contentText}>
            Target type:{" "}
            <strong style={{ color: GreyState }}>{target_type}</strong>
            {/* Target type :  */}
          </Typography>
          {scope && (
            <Typography className={classes.contentText}>
              Scope: <strong style={{ color: GreyState }}>{scope}</strong>
              {/* Scope :  */}
            </Typography>
          )}
        </StepContent>
      </Step>

      {currentStep === 2 && (
        <Step active>
          <StepLabel
            StepIconComponent={currentStep !== 2 ? MetaStepIcon : undefined}
          >
            <h4 className={classes.titleText}>Active workflow report</h4>
          </StepLabel>
          <StepContent>
            <Typography className={classes.contentText}>
              Step :<strong>{selectedStepForWorkflow?.title || ""}</strong>
            </Typography>
          </StepContent>
        </Step>
      )}

      <Step
        active
        completed={currentStep >= 3 || field || custom_field1 ? true : false}
      >
        <StepLabel
          StepIconComponent={
            currentStep < 3 && !(field || custom_field1)
              ? MetaStepIcon
              : undefined
          }
        >
          <h4
            className={
              currentStep < 3 && !(field || custom_field1)
                ? classes.disabledTitleText
                : classes.titleText
            }
          >
            Field
          </h4>
        </StepLabel>

        <StepContent>
          {field && (
            <Typography
              className={
                currentStep < 3 && !(field || custom_field1)
                  ? classes.disabledContent
                  : classes.contentText
              }
            >
              {" "}
              Metadata : <strong>{field}</strong>
            </Typography>
          )}

          {custom_field1 && (
            <Typography
              className={
                currentStep < 3 && !(field || custom_field1)
                  ? classes.disabledContent
                  : classes.contentText
              }
            >
              Custom field : <strong>{custom_field1}</strong>
            </Typography>
          )}
        </StepContent>

        {option_tag && !custom_field2 && (
          <StepContent>
            <Typography className={classes.contentText}>
              Option : <strong>{option_tag}</strong>
            </Typography>
          </StepContent>
        )}
      </Step>

      {currentStep >= 3 && custom_field2 && (
        <Step active>
          <StepLabel
            StepIconComponent={currentStep < 3 ? MetaStepIcon : undefined}
          >
            <h4
              className={
                currentStep < 3 ? classes.disabledTitleText : classes.titleText
              }
            >
              Sub-Field
            </h4>
          </StepLabel>
          <StepContent>
            {field && (
              <Typography
                className={
                  currentStep < 3
                    ? classes.disabledContent
                    : classes.contentText
                }
              >
                Metadata : <strong>{field}</strong>
              </Typography>
            )}
            {custom_field2 && (
              <Typography
                className={
                  currentStep < 3
                    ? classes.disabledContent
                    : classes.contentText
                }
              >
                Custom field : <strong>{custom_field2}</strong>
              </Typography>
            )}
            {option_tag && (
              <Typography
                className={
                  currentStep < 3
                    ? classes.disabledContent
                    : classes.contentText
                }
              >
                Option : <strong>{option_tag}</strong>
              </Typography>
            )}
          </StepContent>
        </Step>
      )}

      <Step completed={currentStep === 4 || defaultValue ? true : false} active>
        <StepLabel
          StepIconComponent={
            currentStep !== 4 && !defaultValue ? MetaStepIcon : undefined
          }
        >
          <h4
            className={
              currentStep !== 4 && !defaultValue
                ? classes.disabledTitleText
                : classes.titleText
            }
          >
            <span style={{ whiteSpace: "nowrap" }}>Default Value</span>{" "}
            <span style={{ fontWeight: 400 }}>(optional)</span>
          </h4>
        </StepLabel>
        <StepContent>
          {defaultValue && (
            <Typography
              className={
                currentStep !== 4 && !defaultValue
                  ? classes.disabledContent
                  : classes.contentText
              }
            >
              Value : <strong>{defaultValue}</strong>
            </Typography>
          )}
        </StepContent>
      </Step>
    </Stepper>
  );
};

export default MetaExpressionStepper;
